import {
  cloneElement, isValidElement, useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode,
} from 'react';
import '../../backlog/backlog.css';
import '../../modules/SettingsDetailPage.css';
import './invoicing.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { useConfirm, useDeleteFlow, useSaveFlow } from '../../../components/confirm';
import { formatCombo, useHotkeys } from '../../../components/hotkeys';
import { useNotify } from '../../../components/notifications';
import { UsDateInput } from '../../../components/UsDateInput';
import { guardedExit, useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { fmtLocalDate } from '../../../utils/datetime';
import { ItemChargePickerDialog } from '../quotes/ItemChargePickerDialog';
import { CHARGE_TYPE, type ItemChargeCandidate } from '../quotes/detailTypes';
import {
  apiErrorMessage, calculateSavedInvoice, deleteInvoices, fetchSavedInvoice, previewSavedInvoice,
  reviewInvoices, saveReviewedInvoice,
} from './api';
import type {
  InvoiceHeaderInput, InvoiceItemInput, InvoiceItemRow, InvoiceNoteInput, InvoiceOption, InvoiceReviewSaveRequest,
  InvoiceTotals, SavedChargeRow, SavedInvoice, SavedInvoiceNote,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Accounting ▸ Invoicing ▸ Invoice Validation — the DETAIL (legacy
// Invoicing_DetailView_Review, and Invoicing_DetailView_Review_MultipleJobID when
// the invoice has more than one JobID; Model.xafml :25053 / :25529).
//
// What the reviewer may change, as legacy: Date Invoiced, Invoiced By, Remark,
// Payment Method, the Bill To / Report To addresses; Discount % on the analysis
// rows (and Description on a multiple-job invoice); Qty / Final Amount on item
// rows; notes (add, and remove one's own — §14a Rule C). Everything else is
// read-only. NO PRICE IS COMPUTED HERE: every edit asks the server
// (POST validation/{id}/calculate) and Save stores what the server re-totals.
// Save re-renders the stored PDF so Review mails what is on screen (legacy
// does not — a reviewed price change could mail a stale PDF).
//
// Action bar (top and bottom): Save · Save and Close · Review · Preview · Delete ·
// Cancel; History opens it read-only (Preview · Cancel).
// AUDIT. FormName "Invoice Validation" — one row per changed value, server-side.
// ─────────────────────────────────────────────────────────────────────────────

const FORM_NAME = 'Invoice Validation';
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

type Tab = 'analysis' | 'items' | 'notes';
interface RowEdit { discount: number; description: string | null }
interface ItemEdit { qty: number; discount: number; finalAmountOverride: number | null }
interface NewNote extends InvoiceNoteInput { date: string }

interface Props {
  id: string;
  /** The list's ids, for the prev / next arrows. */
  ids: string[];
  /** History: the invoice is already reviewed — nothing is editable. */
  readOnly?: boolean;
  onNavigate: (id: string) => void;
  onClose: () => void;
}

function headerInputOf(h: SavedInvoice['header']): InvoiceHeaderInput {
  return {
    dateInvoiced: h.dateInvoiced, invoicedById: h.invoicedById, paymentMethodId: h.paymentMethodId,
    po: h.po, remark: h.remark, email: h.email,
    billStreet1: h.billStreet1, billStreet2: h.billStreet2, billCity: h.billCity,
    billState: h.billState, billZipCode: h.billZipCode, billCountry: h.billCountry,
    sameAddress: h.sameAddress,
    reportStreet1: h.reportStreet1, reportStreet2: h.reportStreet2, reportCity: h.reportCity,
    reportState: h.reportState, reportZipCode: h.reportZipCode, reportCountry: h.reportCountry,
  };
}

function num(text: string): number | null {
  const t = text.trim().replace(/[$,%]/g, '');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export default function InvoiceValidationDetailPage({ id, ids, readOnly = false, onNavigate, onClose }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const { confirm } = useConfirm();

  const [saved, setSaved] = useState<SavedInvoice | null>(null);
  const [employees, setEmployees] = useState<InvoiceOption[]>([]);
  const [header, setHeader] = useState<InvoiceHeaderInput | null>(null);
  const [rowEdits, setRowEdits] = useState<Record<string, RowEdit>>({});
  const [itemEdits, setItemEdits] = useState<Record<string, ItemEdit>>({});
  const [rows, setRows] = useState<SavedChargeRow[]>([]);
  const [items, setItems] = useState<InvoiceItemRow[]>([]);
  const [totals, setTotals] = useState<InvoiceTotals | null>(null);
  const [newNotes, setNewNotes] = useState<NewNote[]>([]);
  const [deletedNotes, setDeletedNotes] = useState<string[]>([]);
  const [noteSel, setNoteSel] = useState<Set<string | number>>(new Set());
  const [noteDraft, setNoteDraft] = useState<{ title: string; text: string } | null>(null);
  const [tab, setTab] = useState<Tab>('analysis');
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [addrOpen, setAddrOpen] = useState(false);
  // Item charges added from the price list / stored ones removed (New Invoice's Add · Remove).
  const [newItems, setNewItems] = useState<InvoiceItemInput[]>([]);
  const [deletedItems, setDeletedItems] = useState<string[]>([]);
  const [itemSel, setItemSel] = useState<Set<string | number>>(new Set());
  const [pickItems, setPickItems] = useState(false);

  useUnsavedGuard(dirty);

  const applySaved = useCallback((inv: SavedInvoice) => {
    setSaved(inv);
    setHeader(headerInputOf(inv.header));
    setRows(inv.rows);
    setItems(inv.items);
    setTotals({
      detailedAmount: inv.header.detailedAmount, discountAmount: inv.header.discountAmount,
      discount: inv.header.discount, amount: inv.header.amount,
    });
    setRowEdits({}); setItemEdits({}); setNewNotes([]); setDeletedNotes([]); setNoteSel(new Set());
    setNewItems([]); setDeletedItems([]); setItemSel(new Set());
    setDirty(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchSavedInvoice(id)
      .then((res) => { if (!cancelled) { setEmployees(res.employees); applySaved(res.invoice); } })
      .catch((e) => { if (!cancelled) notify.error(apiErrorMessage(e) ?? 'The invoice could not be loaded.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, applySaved, notify]);

  const h = saved?.header;
  const editable = !readOnly && h?.statusCode === 2;

  function request(over?: {
    rowEdits?: Record<string, RowEdit>; itemEdits?: Record<string, ItemEdit>;
    newItems?: InvoiceItemInput[]; deletedItems?: string[];
  }): InvoiceReviewSaveRequest {
    const re = over?.rowEdits ?? rowEdits;
    const ie = over?.itemEdits ?? itemEdits;
    return {
      lockVersion: h?.lockVersion ?? 0,
      header: header ?? undefined,
      rows: Object.entries(re).map(([oid, e]) => ({ oid, discount: e.discount, description: e.description })),
      items: Object.entries(ie).map(([oid, e]) => ({ oid, ...e })),
      newItems: over?.newItems ?? newItems,
      deletedItemIds: over?.deletedItems ?? deletedItems,
      newNotes: newNotes.map(({ oid, title, text }) => ({ oid, title, text })),
      deletedNoteIds: deletedNotes,
    };
  }

  async function reprice(req: InvoiceReviewSaveRequest) {
    try {
      const r = await calculateSavedInvoice(id, req);
      setRows(r.rows); setItems(r.items); setTotals(r.totals);
    } catch (e) {
      notify.error(apiErrorMessage(e) ?? 'The invoice could not be recalculated.');
    }
  }

  function patch(p: Partial<InvoiceHeaderInput>) {
    setHeader((cur) => (cur ? { ...cur, ...p } : cur));
    setDirty(true);
  }

  function commitRowCell(row: SavedChargeRow, column: AppColumn<SavedChargeRow>, text: string) {
    const cur = rowEdits[row.oid] ?? { discount: row.discount, description: row.description };
    let next: RowEdit;
    if (column.field === 'discount') {
      const d = num(text);
      if (d === null || d < 0 || d > 100) { notify.warning('Discount must be between 0 and 100%.'); return; }
      next = { ...cur, discount: d };
    } else if (column.field === 'description') {
      next = { ...cur, description: text.trim() || null };
    } else return;
    const all = { ...rowEdits, [row.oid]: next };
    setRowEdits(all); setDirty(true);
    void reprice(request({ rowEdits: all }));
  }

  function commitItemCell(item: InvoiceItemRow, column: AppColumn<InvoiceItemRow>, text: string) {
    const added = newItems.find((n) => n.oid === item.oid);
    if (added) {
      let next: InvoiceItemInput;
      if (column.field === 'qty') {
        const q = num(text);
        if (q === null || q <= 0 || !Number.isInteger(q)) { notify.warning('Qty value must be greater than 0.'); return; }
        next = { ...added, qty: q, finalAmountOverride: null };
      } else if (column.field === 'finalAmount') {
        const f = num(text);
        if (f === null || f < 0) { notify.warning('Final Amount must be a number.'); return; }
        next = { ...added, finalAmountOverride: f };
      } else return;
      const all = newItems.map((n) => (n.oid === item.oid ? next : n));
      setNewItems(all); setDirty(true);
      void reprice(request({ newItems: all }));
      return;
    }
    const cur = itemEdits[item.oid] ?? { qty: item.qty, discount: item.discount, finalAmountOverride: null };
    let next: ItemEdit;
    if (column.field === 'qty') {
      const q = num(text);
      if (q === null || q <= 0 || !Number.isInteger(q)) { notify.warning('Qty value must be greater than 0.'); return; }
      next = { ...cur, qty: q, finalAmountOverride: null };
    } else if (column.field === 'finalAmount') {
      const f = num(text);
      if (f === null || f < 0) { notify.warning('Final Amount must be a number.'); return; }
      next = { ...cur, finalAmountOverride: f };
    } else return;
    const all = { ...itemEdits, [item.oid]: next };
    setItemEdits(all); setDirty(true);
    void reprice(request({ itemEdits: all }));
  }

  /** New Invoice's Add (legacy :726-737): one row per picked price, Qty 1, at the list price. */
  function addItemCharges(picked: ItemChargeCandidate[]) {
    setPickItems(false);
    if (picked.length === 0) return;
    const all = [...newItems, ...picked.map((c) => ({
      oid: crypto.randomUUID(), itemPriceId: c.itemPriceId, qty: 1, discount: 0, finalAmountOverride: null,
    }))];
    setNewItems(all); setDirty(true);
    void reprice(request({ newItems: all }));
  }

  function removeItems() {
    if (itemSel.size === 0) return;
    const picked = [...itemSel].map(String);
    const keptNew = newItems.filter((n) => !picked.includes(n.oid));
    const gone = [...deletedItems, ...picked.filter((oid) => (saved?.items ?? []).some((i) => i.oid === oid))];
    const edits = Object.fromEntries(Object.entries(itemEdits).filter(([oid]) => !picked.includes(oid)));
    setNewItems(keptNew); setDeletedItems(gone); setItemEdits(edits); setItemSel(new Set()); setDirty(true);
    void reprice(request({ newItems: keptNew, deletedItems: gone, itemEdits: edits }));
  }

  async function save(close: boolean) {
    if (!saved || !editable) return;
    await runSave({
      formName: FORM_NAME,
      auditAction: 'edit',
      save: async () => { applySaved(await saveReviewedInvoice(id, request())); },
      onSaved: () => { if (close) onClose(); },
      onError: (e) => { const m = apiErrorMessage(e); if (m) notify.error(m); },
    });
  }

  async function review() {
    if (!saved || !editable) return;
    if (dirty) { notify.warning('Save your changes before Review.'); return; }
    const mailed = !!saved.header.email?.trim();
    const ok = await confirm({
      title: 'Review',
      message: mailed
        ? `Review ${saved.header.invoiceID}? It is e-mailed to ${saved.header.email} and marked Delivered.`
        : `Review ${saved.header.invoiceID}? It goes to Invoice Delivery.`,
    });
    if (!ok) return;
    try {
      const res = await reviewInvoices([id]);
      if (res.failed > 0) { notify.error(res.message); return; }
      notify.success(res.message);
      onClose();
    } catch (e) {
      notify.error(apiErrorMessage(e) ?? 'The invoice could not be reviewed.');
    }
  }

  async function remove() {
    if (!saved || !editable) return;
    await runDelete({
      formName: FORM_NAME,
      message: `Delete ${saved.header.invoiceID}? Its tests go back to the Invoice Queue.`,
      delete: () => deleteInvoices([id]),
      onDeleted: () => { setDirty(false); onClose(); },
      onError: (e) => { const m = apiErrorMessage(e); if (m) notify.error(m); },
    });
  }

  async function preview() {
    if (dirty) notify.info('Preview shows the saved invoice — save first to include your changes.');
    try { await previewSavedInvoice(id); }
    catch (e) { notify.error(apiErrorMessage(e) ?? 'The invoice could not be previewed.'); }
  }

  function addNote() {
    if (!noteDraft?.text.trim()) { notify.warning('Enter the note text.'); return; }
    setNewNotes((n) => [...n, { oid: crypto.randomUUID(), title: noteDraft.title.trim() || null, text: noteDraft.text.trim(), date: new Date().toISOString() }]);
    setNoteDraft(null); setDirty(true);
  }

  function removeNotes() {
    const picked = [...noteSel].map(String);
    const theirs = (saved?.notes ?? []).filter((n) => picked.includes(n.oid) && !n.canEdit);
    if (theirs.length > 0) { notify.error('You are not authorized to modify a note created by another author.'); return; }
    setNewNotes((n) => n.filter((x) => !picked.includes(x.oid)));
    setDeletedNotes((d) => [...d, ...picked.filter((p) => (saved?.notes ?? []).some((n) => n.oid === p))]);
    setNoteSel(new Set()); setDirty(true);
  }

  const pos = ids.indexOf(id);
  const go = (delta: number) => {
    const next = ids[pos + delta];
    if (next) guardedExit(() => onNavigate(next));
  };
  const close = () => guardedExit(onClose);

  useHotkeys([
    { combo: 'mod+s', label: 'Save', group: FORM_NAME, run: () => void save(false), disabled: !editable, order: 1 },
    { combo: 'mod+enter', label: 'Save and Close', group: FORM_NAME, run: () => void save(true), disabled: !editable, order: 2 },
    { combo: 'alt+v', label: 'Review', group: FORM_NAME, run: () => void review(), disabled: !editable, order: 3 },
    { combo: 'alt+p', label: 'Preview', group: FORM_NAME, run: () => void preview(), order: 4 },
    { combo: 'alt+d', label: 'Delete', group: FORM_NAME, run: () => void remove(), disabled: !editable, order: 5 },
    { combo: 'alt+left', label: 'Previous record', group: FORM_NAME, run: () => go(-1), disabled: pos <= 0, order: 6 },
    { combo: 'alt+right', label: 'Next record', group: FORM_NAME, run: () => go(1), disabled: pos < 0 || pos >= ids.length - 1, order: 7 },
    { combo: 'esc', label: 'Cancel', group: FORM_NAME, run: close, order: 8 },
  ]);

  // ── Grids ────────────────────────────────────────────────────────────────
  const multi = !!h?.multipleJobs;
  // G5's grids, their columns in their order (Model.xafml): a single-JobID
  // invoice uses InvoicingAnalysisCharge_ListView_Invoice_Review — Qty ·
  // PriceCode · Matrix · Test · Method · Component · Parameter · ChargeType ·
  // Priority · TAT · UnitPrice · TierNo · From · To · Price · Discount% · Amount;
  // a multiple-JobID invoice uses the Queue grid — … Method · Description ·
  // Component · Parameter · ChargeType · TAT · Priority · UnitPrice · From · To ·
  // TierNo · Price · Discount% · Amount. Qty, PriceCode, Matrix and Test are
  // frozen (:3704-3729). Editable: Discount%, and Description (multiple-JobID).
  const rowColumns = useMemo<AppColumn<SavedChargeRow>[]>(() => {
    const c: Record<string, AppColumn<SavedChargeRow>> = {
      qty:       { field: 'qty',       header: 'Qty',       width: 60, align: 'right', type: 'number' },
      priceCode: { field: 'priceCode', header: 'PriceCode', width: 100 },
      matrix:    { field: 'matrix',    header: 'Matrix',    width: 90 },
      test:      { field: 'test',      header: 'Test',      width: 170, wrap: true },
      method:    { field: 'method',    header: 'Method',    width: 120 },
      description: { field: 'description', header: 'Description', width: 160,
        edit: editable ? { type: 'text' } : undefined },
      component: { field: 'component', header: 'Component', width: 100 },
      parameter: { field: 'parameter', header: 'Parameter', width: 90 },
      chargeType: { field: 'chargeType', header: 'ChargeType', width: 90, render: (r) => CHARGE_TYPE[r.chargeType] ?? '' },
      tat:       { field: 'tat',       header: 'TAT',       width: 80 },
      priority:  { field: 'priority',  header: 'Priority',  width: 90 },
      unitPrice: { field: 'unitPrice', header: 'UnitPrice', width: 95, align: 'right', type: 'number', render: (r) => money.format(r.unitPrice),
        summary: (list) => money.format(list.reduce((n, r) => n + r.unitPrice, 0)) },
      from:      { field: 'from',      header: 'From',      width: 60, align: 'right', type: 'number', render: (r) => String(r.from ?? 0) },
      to:        { field: 'to',        header: 'To',        width: 60, align: 'right', type: 'number', render: (r) => String(r.to ?? 0) },
      tierNo:    { field: 'tierNo',    header: 'TierNo',    width: 65, align: 'right', type: 'number', render: (r) => String(r.tierNo ?? 0) },
      tierPrice: { field: 'tierPrice', header: 'Price',     width: 100, align: 'right', type: 'number', render: (r) => money.format(r.tierPrice),
        summary: (list) => money.format(list.reduce((n, r) => n + r.tierPrice, 0)) },
      discount:  { field: 'discount',  header: 'Discount%', width: 90, align: 'right', type: 'number',
        edit: editable ? { type: 'number' } : undefined, render: (r) => r.discount.toFixed(2) },
      amount:    { field: 'amount',    header: 'Amount',    width: 105, align: 'right', type: 'number',
        render: (r) => money.format(r.amount), summary: (list) => money.format(list.reduce((n, r) => n + r.amount, 0)) },
    };
    const order = multi
      ? ['qty', 'priceCode', 'matrix', 'test', 'method', 'description', 'component', 'parameter', 'chargeType',
         'tat', 'priority', 'unitPrice', 'from', 'to', 'tierNo', 'tierPrice', 'discount', 'amount']
      : ['qty', 'priceCode', 'matrix', 'test', 'method', 'component', 'parameter', 'chargeType',
         'priority', 'tat', 'unitPrice', 'tierNo', 'from', 'to', 'tierPrice', 'discount', 'amount'];
    return order.map((k) => c[k]!);
  }, [multi, editable]);

  const itemColumns = useMemo<AppColumn<InvoiceItemRow>[]>(() => [
    { field: 'itemCode',    header: 'ItemCode',    width: 110 },
    { field: 'qty',         header: 'Qty',         width: 65, align: 'right', type: 'number', edit: editable ? { type: 'number' } : undefined },
    { field: 'itemName',    header: 'Item',        width: 170 },
    { field: 'description', header: 'Description', width: 190 },
    { field: 'category',    header: 'Category',    width: 120 },
    { field: 'unit',        header: 'Unit',        width: 80 },
    // G5 binds this column to UnitPrice (the price after the item's discount).
    { field: 'unitPrice',   header: 'UnitPrice',   width: 100, align: 'right', type: 'number', render: (r) => money.format(r.unitPrice) },
    { field: 'amount',      header: 'Amount',      width: 100, align: 'right', type: 'number', render: (r) => money.format(r.amount) },
    { field: 'discount',    header: 'Discount',    width: 90, align: 'right', type: 'number', render: (r) => r.discount.toFixed(2) },
    { field: 'finalAmount', header: 'FinalAmount', width: 110, align: 'right', type: 'number',
      edit: editable ? { type: 'number' } : undefined, render: (r) => money.format(r.finalAmount),
      summary: (list) => money.format(list.reduce((n, i) => n + i.finalAmount, 0)) },
  ], [editable]);

  type NoteRow = {
    oid: string; title: string | null; text: string | null; author: string | null; date: string | null;
    attachmentName?: string | null; isCaseNarrative?: boolean; isNew?: boolean;
  };
  const noteRows: NoteRow[] = [
    ...(saved?.notes ?? []).filter((n: SavedInvoiceNote) => !deletedNotes.includes(n.oid))
      .map((n) => ({ oid: n.oid, title: n.title, text: n.text, author: n.author, date: n.date,
        attachmentName: n.attachmentName, isCaseNarrative: n.isCaseNarrative })),
    ...newNotes.map((n) => ({ oid: n.oid, title: n.title, text: n.text, author: 'You (not saved yet)', date: n.date, isNew: true })),
  ];
  // G5 Invoicing_Notes_ListView (Notes BO list members, in order).
  const noteColumns: AppColumn<NoteRow>[] = [
    { field: 'title',  header: 'Title',  width: 180 },
    { field: 'attachmentName', header: 'Attachment', width: 160 },
    { field: 'text',   header: 'Note' },
    { field: 'author', header: 'Author', width: 170 },
    { field: 'date',   header: 'Date',   width: 150, type: 'datetime' },
    { field: 'isCaseNarrative', header: 'IsCaseNarrative', width: 120, align: 'center',
      render: (r) => <input type="checkbox" checked={!!r.isCaseNarrative} readOnly disabled aria-label="IsCaseNarrative" /> },
  ];

  // ── Field helpers — the shared detail-view markup (rule 12) ───────────────
  function field(label: string, control: ReactNode, opts: { tall?: boolean } = {}) {
    return (
      <div className={`sd-row${opts.tall ? ' sd-row--span2 inv-top' : ''}`} key={label}>
        <label className="sd-label">{label}</label>
        {isValidElement<{ 'aria-label'?: string }>(control) && typeof control.type === 'string'
          ? cloneElement(control, { 'aria-label': label }) : control}
      </div>
    );
  }
  function section(title: string, left: ReactNode[], right: ReactNode[], after?: ReactNode) {
    const slots = (list: ReactNode[]) => list.reduce<number>((n, f) =>
      n + (isValidElement<{ className?: string }>(f) && f.props.className?.includes('sd-row--span2') ? 2 : 1), 0);
    const rowCount = Math.max(slots(left), slots(right));
    const pad = (n: number, side: string) => Array.from({ length: rowCount - n },
      (_, i) => <div className="sd-row" aria-hidden="true" key={`pad-${side}-${i}`} />);
    return (
      <>
        <h3 className="sd-section-h">{title}</h3>
        <div className="sd-grid sd-grid--colmajor sd-grid--side" style={{ '--sd-rows': String(rowCount) } as CSSProperties}>
          {left}{pad(slots(left), 'l')}{right}{pad(slots(right), 'r')}
        </div>
        {after}
      </>
    );
  }
  const ro = (value: string | number | null | undefined) =>
    <input type="text" className="sd-input readonly" readOnly value={value ?? ''} />;
  const text = (key: keyof InvoiceHeaderInput) => (editable && header
    ? <input type="text" className="sd-input" maxLength={100} autoComplete="off"
             value={(header[key] as string | null) ?? ''}
             onChange={(e) => patch({ [key]: e.target.value || null } as Partial<InvoiceHeaderInput>)} />
    : ro(header?.[key] as string | null));
  const report = (billKey: keyof InvoiceHeaderInput, reportKey: keyof InvoiceHeaderInput) =>
    header?.sameAddress ? ro(header[billKey] as string | null) : text(reportKey);

  const tip = (label: string, combo: string) => `${label} (${formatCombo(combo)})`;
  const actionBar = (bottom: boolean) => (
    <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
      {editable && (
        <>
          <button type="button" className="sd-abtn primary" title={tip('Save', 'mod+s')} onClick={() => void save(false)} disabled={loading}>
            <span className="tb-i">💾</span> Save
          </button>
          <button type="button" className="sd-abtn" title={tip('Save and Close', 'mod+enter')} onClick={() => void save(true)} disabled={loading}>
            <span className="tb-i">💾</span> Save and Close
          </button>
          <button type="button" className="sd-abtn" title={tip('Review', 'alt+v')} onClick={() => void review()} disabled={loading}>
            <i className="ti ti-circle-check tb-i-green" aria-hidden="true" /> Review
          </button>
        </>
      )}
      <button type="button" className="sd-abtn" title={tip('Preview', 'alt+p')} onClick={() => void preview()} disabled={loading || !saved}>
        <i className="ti ti-file-type-pdf inv-pdf" aria-hidden="true" /> Preview
      </button>
      {editable && (
        <button type="button" className="sd-abtn" title={tip('Delete', 'alt+d')} onClick={() => void remove()} disabled={loading}>
          <i className="ti ti-trash tb-i-red" aria-hidden="true" /> Delete
        </button>
      )}
      <span className="sd-abtn-sep" aria-hidden="true" />
      <button type="button" className="sd-abtn cancel" title={tip('Cancel', 'esc')} onClick={close}>
        <span className="tb-i">↩︎</span> Cancel
      </button>
    </div>
  );

  const existingItemIds = useMemo(
    () => new Set(items.map((i) => (i.itemPriceId ?? '').toLowerCase())), [items]);

  const tabs: Array<{ id: Tab; label: string; count: number }> = [
    { id: 'analysis', label: 'Analysis Charge', count: rows.length },
    { id: 'items',    label: 'Item Charge',     count: items.length },
    { id: 'notes',    label: 'Notes',           count: noteRows.length },
  ];

  // Same layout as the Invoice Queue's New Invoice page (InvoiceDetailPage):
  // a summary strip, then Summary without what the strip and title already show
  // (Invoice ID is in the title, Client / Job ID in the strip).
  const quoteBox = h ? (
    <div className="inv-robox" title="Quote ID · Quoted Date · Quoted By">
      {h.quoteCode
        ? <><b>{h.quoteCode}</b><span className="inv-dot">·</span>{fmtLocalDate(h.quotedDate)}
            <span className="inv-dot">·</span>{h.quotedByName ?? ''}</>
        : <span className="inv-muted">No quote on this invoice</span>}
    </div>
  ) : null;
  const delayBox = h ? (
    <div className="inv-robox" title="Days from the requested due date to today, as ALPACA counts it">
      {(h.daysDelayed ?? 0) > 0
        ? <span className="inv-pill inv-pill--late">{h.daysDelayed} days late</span>
        : <span className="inv-pill inv-pill--ok">On time</span>}
    </div>
  ) : null;
  // Balanced like the New Invoice page (owner 2026-10-26): billing on the left,
  // schedule, pricing and status on the right. Invoice ID first, as G5. Single
  // JobID 9 | 8 slots; several JobIDs (G5 shows no project / quote / due date /
  // TAT / priority there) 4 | 4.
  const invoicedDateField = h && header ? field('Invoiced Date', editable
    ? <UsDateInput className="sd-input" value={(header.dateInvoiced ?? '').slice(0, 10)}
                   onChange={(v) => patch({ dateInvoiced: v || null })} />
    : ro(fmtLocalDate(h.dateInvoiced))) : null;
  const invoicedByField = h && header ? field('Invoiced By', editable
    ? <select className="sd-input" value={header.invoicedById ?? ''} title="Alt+↓ to open"
              onChange={(e) => patch({ invoicedById: e.target.value || null })}>
        <option value="" />
        {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
      </select>
    : ro(employees.find((e) => e.id === h.invoicedById)?.name ?? '')) : null;
  const paymentField = h && header ? field('Payment Method', editable
    ? <select className="sd-input" value={header.paymentMethodId ?? ''} title="Alt+↓ to open"
              onChange={(e) => patch({ paymentMethodId: e.target.value || null })}>
        <option value="" />
        {(saved?.paymentMethods ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    : ro(saved?.paymentMethods.find((p) => p.id === header.paymentMethodId)?.name ?? '')) : null;
  const remarkField = h && header ? field('Remark', editable
    ? <textarea rows={2} className="sd-input sd-input--tall" value={header.remark ?? ''} onChange={(e) => patch({ remark: e.target.value || null })} />
    : <textarea rows={2} className="sd-input sd-input--tall readonly" readOnly value={header.remark ?? ''} />, { tall: true }) : null;

  const summaryLeft = h && header ? (multi
    ? [field('Invoice ID', ro(h.invoiceID)), invoicedDateField, invoicedByField, paymentField]
    : [
      field('Invoice ID', ro(h.invoiceID)),
      invoicedDateField,
      invoicedByField,
      field('Project ID', ro(h.projectCode)),
      field('Project Name', ro(h.projectName)),
      paymentField,
      field('PO #', text('po')),
      remarkField,
    ]) : [];
  const summaryRight = h && header ? (multi
    ? [field('PO #', text('po')), field('Status', ro(h.status)), remarkField]
    : [
      field('Quote', quoteBox),
      // Requested / Completed side by side, Completed amber when it is later.
      field('Due Date',
        <div className="inv-pair">
          <label><small>Requested</small>{ro(fmtLocalDate(h.dueDate))}</label>
          <label><small>Completed</small>
            <input type="text" readOnly value={fmtLocalDate(h.dueDateCompleted)}
                   className={`sd-input readonly${h.dueDate && h.dueDateCompleted && h.dueDateCompleted > h.dueDate ? ' inv-late-box' : ''}`} />
          </label>
        </div>, { tall: true }),
      field('TAT', ro(h.tatName)),
      field('Priority', ro(h.priorityName)),
      field('Days Delayed', delayBox),
      field('Actual Priority Applied', ro(h.actualPriorityName)),
      field('Status', ro(h.status)),
    ]) : [];
  const billLine = header ? [header.billStreet1, header.billStreet2, header.billCity,
    [header.billState, header.billZipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ') : '';
  const jobs = (h?.jobIdText ?? '').split(';').map((j) => j.trim()).filter(Boolean);

  return (
    <div className="bl-page mlv-host inv-detail">
      <div className="bl-grid-area">
        <div className="sd-wrap">
          <div className="sd-head">
            <div>
              <h2>{readOnly ? 'Invoice Review' : 'Invoice Validation'} – {h?.invoiceID ?? ''}</h2>
              <div className="sd-bc">
                <span>Accounting</span><i className="ti ti-chevron-right" />
                <span>Invoicing</span><i className="ti ti-chevron-right" />
                <a onClick={close}>Invoice Validation</a><i className="ti ti-chevron-right" />
                <span>{h?.invoiceID ?? ''}</span>
              </div>
            </div>
            <div className="sd-navarrows">
              <button type="button" className="sd-na" onClick={() => go(-1)} disabled={pos <= 0}
                      title={tip('Previous record', 'alt+left')}>‹</button>
              <button type="button" className="sd-na next" onClick={() => go(1)} disabled={pos < 0 || pos >= ids.length - 1}
                      title={tip('Next record', 'alt+right')}>›</button>
            </div>
          </div>

          {actionBar(false)}

          <div className="sd-body">
            {h?.rollbackReason && h.statusCode === 2 && (
              <div className="inv-note" role="status">Rolled back: {h.rollbackReason}</div>
            )}
            {/* Summary strip — what is being billed, at a glance. */}
            <div className="inv-strip">
              <div className="inv-tile"><div className="inv-k">Client</div><div className="inv-v">{h?.clientName ?? ''}</div></div>
              <div className="inv-tile">
                <div className="inv-k">Jobs</div>
                <div>{jobs.map((j) => <span className="inv-chip" key={j}>{j}</span>)}</div>
              </div>
              <div className="inv-tile"><div className="inv-k">Tests</div><div className="inv-v">{rows.length}</div></div>
              <div className="inv-tile"><div className="inv-k">Amount</div><div className="inv-big">{money.format(totals?.amount ?? 0)}</div></div>
            </div>

            {section('Summary', summaryLeft, summaryRight)}

            {h && (
              <div className="inv-contacts">
                {section('Contacts', [
                  field('Primary Contact', ro(h.primaryContactName)),
                  field('Contact Email', ro(h.contactEmail)),
                ], [
                  field('Contact Phone', ro(h.contactPhone)),
                  field('Invoice Email', <input type="text" className="sd-input readonly" readOnly value={h.email ?? ''}
                    title="Review e-mails the invoice to these addresses and marks it Delivered" />),
                ])}
              </div>
            )}

            {/* Bill To / Report To — one summary line until Edit, as on New Invoice. */}
            {header && (
              <>
                <h3 className="sd-section-h inv-addr-h">
                  Addresses
                  <button type="button" className="inv-addredit" onClick={() => setAddrOpen(!addrOpen)}
                          aria-expanded={addrOpen}>
                    {addrOpen ? 'Collapse' : editable ? 'Edit' : 'Show'}
                  </button>
                </h3>
                {!addrOpen && (
                  <div className="inv-muted inv-addrprev">
                    Bill to: {billLine || '—'} · Report to: {header.sameAddress ? 'same as bill to' : 'its own address'}
                  </div>
                )}
                {addrOpen && (
                  <div>
                    {section('Bill To Address', [
                      field('Street 1', text('billStreet1')), field('City', text('billCity')), field('Country', text('billCountry')),
                    ], [
                      field('Street 2', text('billStreet2')), field('State', text('billState')), field('Zip', text('billZipCode')),
                    ],
                      <div className="sd-grid sd-grid--side inv-same">
                        <div className="sd-row sd-row--check">
                          <span className="sd-label" aria-hidden="true">&nbsp;</span>
                          <label className="sd-check">
                            <input type="checkbox" checked={header.sameAddress} disabled={!editable}
                                   onChange={(e) => patch({ sameAddress: e.target.checked })} />
                            Same Address For Both Shipping And Billing
                          </label>
                        </div>
                      </div>,
                    )}
                    {section('Report To Address', [
                      field('Street 1', report('billStreet1', 'reportStreet1')), field('City', report('billCity', 'reportCity')),
                      field('Country', report('billCountry', 'reportCountry')),
                    ], [
                      field('Street 2', report('billStreet2', 'reportStreet2')), field('State', report('billState', 'reportState')),
                      field('Zip', report('billZipCode', 'reportZipCode')),
                    ])}
                  </div>
                )}
              </>
            )}

            <div className="sd-assoc-tabbar sd-childtabbar" role="tablist">
              {tabs.map((t) => (
                <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
                        className={`sd-assoc-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
                  {t.label}<span className="sd-assoc-tabn">{t.count}</span>
                </button>
              ))}
            </div>

            <div role="tabpanel" className="sd-childtabpanel inv-tabpanel" hidden={tab !== 'analysis'}>
              <AppDataGrid<SavedChargeRow>
                rows={rows}
                columns={rowColumns}
                getRowId={(r) => r.oid}
                loading={loading}
                // G5: Qty, PriceCode, Matrix and Test frozen on the left (:3704-3729).
                stickyColumns={4}
                onCellCommit={commitRowCell}
                maximizable
                exportFilename={`invoice-${h?.invoiceID ?? ''}-analysis-charge`}
                emptyText="No analysis charges."
              />
            </div>

            <div role="tabpanel" className="sd-childtabpanel inv-tabpanel" hidden={tab !== 'items'}>
              {editable && (
                <div className="inv-tabbar">
                  <button type="button" className="ca-btn" onClick={() => setPickItems(true)} disabled={loading}>
                    <i className="ti ti-plus tb-i-green" aria-hidden="true" /> Add
                  </button>
                  <button type="button" className="ca-btn" onClick={removeItems} disabled={itemSel.size === 0}>
                    <i className="ti ti-minus tb-i-red" aria-hidden="true" /> Remove
                  </button>
                </div>
              )}
              <AppDataGrid<InvoiceItemRow>
                rows={items}
                columns={itemColumns}
                getRowId={(r) => r.oid}
                loading={loading}
                selectable={editable}
                selectedIds={itemSel}
                onSelectionChange={setItemSel}
                onCellCommit={commitItemCell}
                maximizable
                exportFilename={`invoice-${h?.invoiceID ?? ''}-item-charge`}
                emptyText="No item charges."
              />
              <div className="inv-sums">
                <span>Total Qty <b>{items.reduce((n, i) => n + i.qty, 0)}</b></span>
                <span>Total Final Amount <b>{money.format(items.reduce((n, i) => n + i.finalAmount, 0))}</b></span>
              </div>
            </div>

            <div role="tabpanel" className="sd-childtabpanel inv-tabpanel" hidden={tab !== 'notes'}>
              {noteRows.length === 0 ? (
                <div className="inv-empty">
                  <i className="ti ti-notes" aria-hidden="true" />
                  <b>{editable ? 'Add a note for the client or the reviewer' : 'No notes on this invoice'}</b>
                  {editable && <>It is saved with the invoice.
                    <div>
                      <button type="button" className="ca-btn" onClick={() => setNoteDraft({ title: '', text: '' })}>
                        <i className="ti ti-plus tb-i-green" aria-hidden="true" /> Add note
                      </button>
                    </div>
                  </>}
                </div>
              ) : (
                <>
                  {editable && (
                    <div className="inv-tabbar">
                      <button type="button" className="ca-btn" onClick={() => setNoteDraft({ title: '', text: '' })}>
                        <i className="ti ti-plus tb-i-green" aria-hidden="true" /> Add
                      </button>
                      <button type="button" className="ca-btn" onClick={removeNotes} disabled={noteSel.size === 0}>
                        <i className="ti ti-minus tb-i-red" aria-hidden="true" /> Remove
                      </button>
                      <span className="inv-hint-r">Notes are saved with the invoice; only the author can remove one.</span>
                    </div>
                  )}
                  <AppDataGrid<NoteRow>
                    rows={noteRows}
                    columns={noteColumns}
                    getRowId={(r) => r.oid}
                    selectable={editable}
                    selectedIds={noteSel}
                    onSelectionChange={setNoteSel}
                    exportFilename={`invoice-${h?.invoiceID ?? ''}-notes`}
                  />
                </>
              )}
            </div>
          </div>

          <div className="inv-sticky">
            <div className="inv-totals" aria-label="Totals">
              <span title="Analysis Charge quantities (samples × tests); item charges have their own total on the Item Charge tab">Test Qty <b>{rows.reduce((n, r) => n + r.qty, 0)}</b></span>
              <span>Detailed Amount <b>{money.format(totals?.detailedAmount ?? 0)}</b></span>
              <span className="inv-disc">Discount <b>−{money.format(totals?.discountAmount ?? 0)}</b>
                <span className="inv-muted"> ({(totals?.discount ?? 0).toFixed(2)}%)</span></span>
              <span className="inv-grand">Amount <b>{money.format(totals?.amount ?? 0)}</b></span>
            </div>
            {actionBar(true)}
          </div>
        </div>
      </div>

      <ItemChargePickerDialog
        open={pickItems}
        existingIds={existingItemIds}
        onAdd={addItemCharges}
        onClose={() => setPickItems(false)}
      />

      <AppDialog
        open={noteDraft !== null}
        title="Add Note"
        icon="ti-notes"
        width={520}
        onClose={() => setNoteDraft(null)}
        footer={(
          <>
            <button type="button" className="ca-btn ca-btn-solid" onClick={addNote}>OK</button>
            <button type="button" className="ca-btn" onClick={() => setNoteDraft(null)}>Cancel</button>
          </>
        )}
      >
        <div className="inv-notegrid">
          <label htmlFor="inv-rv-note-title">Title</label>
          <input id="inv-rv-note-title" type="text" className="sd-input" maxLength={100}
                 value={noteDraft?.title ?? ''} onChange={(e) => setNoteDraft((d) => (d ? { ...d, title: e.target.value } : d))} />
          <label htmlFor="inv-rv-note-text">Note<span className="req">*</span></label>
          <textarea id="inv-rv-note-text" rows={5} className={`sd-input${noteDraft?.text.trim() ? '' : ' required-empty'}`}
                    value={noteDraft?.text ?? ''} onChange={(e) => setNoteDraft((d) => (d ? { ...d, text: e.target.value } : d))} />
        </div>
      </AppDialog>
    </div>
  );
}
